import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { LogIn, LogOut, Crown } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { navCategories } from './navConfig';

// Desktop-only left sidebar (>= lg). Categorized by section.
// Width ~240px, fixed on the left. Active item highlighted with brand color.
const Sidebar = ({ badges = {} }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isPro } = useAuth();

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);

  const resolvePath = (item) => (item.authAware && !user ? '/auth' : item.path);

  return (
    <aside
      className="w-60 bg-white border-r border-[#E2E8F0] h-screen fixed left-0 top-0 flex-col z-40 hidden lg:flex"
      data-testid="desktop-sidebar"
      aria-label="Menu lateral"
    >
      {/* Brand */}
      <div className="px-5 py-4 border-b border-[#E2E8F0]">
        <Link to="/" className="flex items-center gap-2.5" data-testid="sidebar-logo">
          <img src="/logo.png" alt="Tô de Olho" className="h-9 w-9 rounded-lg" />
          <span className="text-base font-black text-[#006030] tracking-tight">Tô de Olho</span>
        </Link>
      </div>

      {/* Categorized nav */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-5 custom-scrollbar">
        {navCategories.map((cat) => (
          <div key={cat.title}>
            <div className="px-2 mb-1.5 text-[10px] font-bold tracking-wider text-[#94A3B8]">
              {cat.title}
            </div>
            <div className="space-y-0.5">
              {cat.items.map((item) => {
                const Icon = item.icon;
                const active = isActive(item.path);
                const badge = item.badgeKey ? badges[item.badgeKey] || 0 : 0;
                return (
                  <Link
                    key={item.path + item.label}
                    to={resolvePath(item)}
                    data-testid={`sidebar-${item.testId}`}
                    className={cn(
                      'flex items-center gap-2.5 px-2.5 py-2 rounded-md text-[13px] font-medium transition-all duration-150 relative',
                      active
                        ? 'bg-[#E8F5E9] text-[#006030] font-semibold'
                        : 'text-[#475569] hover:bg-[#F4F6F8] hover:text-[#006030]',
                    )}
                    aria-current={active ? 'page' : undefined}
                  >
                    <Icon className="w-4 h-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                    {badge > 0 && (
                      <span
                        className="ml-auto min-w-[18px] h-4 flex items-center justify-center rounded-full bg-[#C62828] text-white text-[9px] font-bold px-1.5"
                        data-testid={`sidebar-${item.testId}-badge`}
                      >
                        {badge > 99 ? '99+' : badge}
                      </span>
                    )}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      {/* Footer / auth */}
      <div className="border-t border-[#E2E8F0] p-3 space-y-2">
        {user ? (
          <div className="flex items-center gap-2.5 px-1">
            <div className="w-8 h-8 rounded-full bg-[#006030] text-white flex items-center justify-center text-xs font-bold shrink-0">
              {(user.nome || user.email || '?').slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[12px] font-semibold text-[#0F1C2E] truncate" data-testid="sidebar-user-name">
                {user.nome || user.email}
              </p>
              <div className="flex items-center gap-1">
                {isPro && <Crown className="w-3 h-3 text-[#F0D000]" />}
                <span className="text-[10px] text-[#94A3B8]">{isPro ? 'PRO' : 'Gratuito'}</span>
              </div>
            </div>
            <button
              onClick={logout}
              className="p-1.5 rounded-md hover:bg-[#FEE2E2] text-[#C62828] transition-colors"
              data-testid="sidebar-logout-btn"
              aria-label="Sair"
              title="Sair"
            >
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => navigate('/auth')}
            className="w-full flex items-center justify-center gap-2 py-2 bg-[#006030] text-white text-xs font-semibold rounded-md hover:bg-[#004020] transition-colors"
            data-testid="sidebar-login-btn"
          >
            <LogIn className="w-3.5 h-3.5" /> Entrar / Criar conta
          </button>
        )}
        {!isPro && user && (
          <button
            onClick={() => navigate('/planos')}
            className="w-full py-1.5 text-[11px] font-bold text-[#006030] hover:bg-[#E8F5E9] rounded-md transition-colors"
            data-testid="sidebar-upgrade-btn"
          >
            Fazer upgrade PRO
          </button>
        )}
        <div className="flex justify-center gap-2 text-[10px] text-[#94A3B8] pt-1">
          <Link to="/termos" className="hover:text-[#006030]">Termos</Link>
          <span>•</span>
          <Link to="/privacidade" className="hover:text-[#006030]">Privacidade</Link>
        </div>
        <p className="text-[10px] text-[#94A3B8] text-center pt-1 leading-tight">
          Inteligência pública para todo cidadão.
        </p>
      </div>
    </aside>
  );
};

export default Sidebar;
